// @ts-nocheck
import React, { useState } from 'react';
import {
  View, Text, ScrollView, TouchableOpacity, StyleSheet, Platform,
} from 'react-native';
import { Colors, FontSize, FontWeight, Radii } from '../../theme/tokens';
import { useResponsive } from '../../theme/responsive';
import { useApp, navigate } from '../../store/AppStore';
import { ScreenShell } from '../../components/WebShell';
import { ComingSoon } from '../../components/ComingSoon';
import { Icon } from '../../components/Icon';
import PrimaryButton from '../../components/Button_Component/Primary_Button';
import { Chip } from '../../components/Chip';

// ─── Locale Tabs ──────────────────────────────────────────────────────────────

const LOCALE_TABS = [
  { code: 'EN', label: 'English' },
  { code: 'TA', label: 'தமிழ்' },
  { code: 'HI', label: 'हिन्दी' },
];

function LocaleTabs({ active, onChange }: { active: string; onChange: (c: string) => void }) {
  return (
    <View style={styles.tabBar}>
      {LOCALE_TABS.map(t => (
        <TouchableOpacity
          key={t.code}
          style={[styles.tab, active === t.code && styles.tabActive]}
          onPress={() => onChange(t.code)}
        >
          <Text style={[styles.tabCode, { color: active === t.code ? Colors.accent : Colors.ink4 }]}>
            {t.code}
          </Text>
          <Text style={[styles.tabLabel, { color: active === t.code ? Colors.ink : Colors.ink3 }]}>
            {t.label}
          </Text>
        </TouchableOpacity>
      ))}
    </View>
  );
}

// ─── TemplatesListScreen ──────────────────────────────────────────────────────

export function TemplatesListScreen() {
  const { templates } = useApp();
  const { isMobile, pagePadX } = useResponsive();
  const [locale, setLocale] = useState('EN');
  const [hoveredRow, setHoveredRow] = useState<string | null>(null);

  const localeLabel = locale === 'EN' ? 'English' : locale === 'TA' ? 'Tamil' : 'Hindi';

  // On mobile the fixed-width table columns can overflow; wrap the table in a
  // horizontal scroll so the layout never breaks. On wider screens render directly.
  const TableWrap = isMobile
    ? (props: any) => (
        <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{ minWidth: 720 }}>
          {props.children}
        </ScrollView>
      )
    : React.Fragment;

  // ── Showing the premium "coming soon" card for now. Original Email Templates
  //    screen preserved below (unreachable) — remove this return to restore. ──
  return (
    <ComingSoon
      crumbs={[{ label: 'Settings' }, { label: 'Email Templates' }]}
      icon="template"
      title="Email Templates"
      subtitle="Craft reusable, on-brand replies your whole team can trust. We're making the editor a joy to use — and it's coming very soon."
    />
  );

  return (
    <ScreenShell crumbs={[
      { label: 'Settings' },
      { label: 'Email Templates' },
    ]}>
      <View style={styles.root}>
        {/* Page Head */}
        <View style={[styles.pageHead, { paddingHorizontal: pagePadX, flexWrap: 'wrap' }]}>
          <View style={{ flex: 1 }}>
            <Text style={styles.pageTitle}>Email Templates</Text>
            <Text style={styles.pageSub}>System emails sent on ticket lifecycle events. Customise per locale to match your brand.</Text>
          </View>
          <PrimaryButton
            title="New template"
            size="md"
            variant="primary"
            leftIcon={<Icon name="Plus" size={14} color={Colors.paper} />}
            onPress={() => toast('Email templates are system-defined. To customise one, click on it and edit.', 'info', 'mail')}
          />
        </View>

        <ScrollView style={styles.body} contentContainerStyle={[styles.bodyContent, { paddingHorizontal: pagePadX }]}>
          <LocaleTabs active={locale} onChange={setLocale} />

          {/* Templates Table */}
          <TableWrap>
          <View style={styles.card}>
            {/* Table Header */}
            <View style={[styles.tableRow, styles.tableHeader]}>
              <View style={{ flex: 1 }}>
                <Text style={styles.tableHeaderText}>Template</Text>
              </View>
              <View style={{ width: 180 }}>
                <Text style={styles.tableHeaderText}>Last edited</Text>
              </View>
              <View style={{ width: 130 }}>
                <Text style={styles.tableHeaderText}>Status</Text>
              </View>
              <View style={{ width: 60 }} />
            </View>

            {(templates || []).map((t: any, idx: number) => (
              <TouchableOpacity
                key={t.slug}
                style={[
                  styles.tableRow,
                  styles.templateRow,
                  idx < (templates || []).length - 1 && styles.rowBorder,
                  hoveredRow === t.slug && styles.rowHovered,
                ]}
                onPress={() => navigate('/admin/settings/email-templates/' + t.slug)}
                {...(Platform.OS === 'web' ? {
                  onMouseEnter: () => setHoveredRow(t.slug),
                  onMouseLeave: () => setHoveredRow(null),
                } as any : {})}
              >
                <View style={[styles.templateCell, { flex: 1 }]}>
                  <View style={[
                    styles.templateIcon,
                    { backgroundColor: t.status === 'customised' ? Colors.accent : Colors.paper3 },
                  ]}>
                    <Icon name="Mail" size={14} color={t.status === 'customised' ? Colors.white : Colors.ink3} />
                  </View>
                  <View style={{ flex: 1 }}>
                    <Text style={styles.templateName}>{t.name}</Text>
                    <Text style={styles.templateDesc} numberOfLines={1}>{t.desc}</Text>
                  </View>
                </View>
                <View style={{ width: 180 }}>
                  {t.editedAgo ? (
                    <Text style={styles.editedText}>
                      {t.editedAgo}
                      <Text style={styles.editedBy}> — {t.editedBy}</Text>
                    </Text>
                  ) : (
                    <Text style={styles.editedNever}>Never edited</Text>
                  )}
                </View>
                <View style={{ width: 130 }}>
                  {t.status === 'customised'
                    ? <Chip label="Customised" tone="accent" dot size="sm" />
                    : <Chip label="Default" tone="default" size="sm" />}
                </View>
                <View style={[styles.tableCell, { width: 60, justifyContent: 'flex-end' }]}>
                  <Icon name="ChevronR" size={13} color={Colors.ink3} />
                </View>
              </TouchableOpacity>
            ))}
          </View>
          </TableWrap>

          {/* Locale note */}
          <View style={styles.localeNote}>
            <Icon name="Eye" size={14} color={Colors.accentInk} />
            <Text style={styles.localeNoteText}>
              You're previewing the <Text style={styles.localeBold}>{localeLabel}</Text> locale. Customers receive templates based on their language preference, falling back to English.
            </Text>
          </View>
        </ScrollView>
      </View>
    </ScreenShell>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────────
const styles = StyleSheet.create({
  root: { flex: 1, backgroundColor: Colors.paper },
  pageHead: {
    flexDirection: 'row', alignItems: 'flex-start', gap: 16,
    paddingHorizontal: 28, paddingTop: 20, paddingBottom: 18,
    borderBottomWidth: 1, borderBottomColor: Colors.line,
    backgroundColor: Colors.card, flexShrink: 0,
  },
  pageTitle: { fontSize: 20, fontWeight: FontWeight.semibold, color: Colors.ink, letterSpacing: -0.2 },
  pageSub: { fontSize: 13, color: Colors.ink3, marginTop: 4 },
  body: { flex: 1 },
  bodyContent: { padding: 20, maxWidth: 1080, alignSelf: 'center', width: '100%' as any },
  tabBar: {
    flexDirection: 'row',
    borderBottomWidth: 1, borderBottomColor: Colors.line,
    marginBottom: 16,
  },
  tab: {
    flexDirection: 'row', alignItems: 'center', gap: 6,
    paddingHorizontal: 14, paddingVertical: 8,
    borderBottomWidth: 2, borderBottomColor: Colors.transparent,
    ...(Platform.OS === 'web' ? { cursor: 'pointer' } as any : {}),
  },
  tabActive: { borderBottomColor: Colors.accent },
  tabCode: { fontSize: 11.5, fontWeight: FontWeight.bold },
  tabLabel: { fontSize: 14 },
  card: {
    backgroundColor: Colors.card,
    borderRadius: Radii.r3,
    borderWidth: 1, borderColor: Colors.line,
    overflow: 'hidden',
  },
  tableRow: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 16, paddingVertical: 10 },
  tableHeader: {
    backgroundColor: Colors.paper2,
    borderBottomWidth: 1, borderBottomColor: Colors.line,
    paddingVertical: 8,
  },
  tableHeaderText: { fontSize: 11.5, fontWeight: FontWeight.semibold, color: Colors.ink3 },
  templateRow: {
    alignItems: 'center',
    ...(Platform.OS === 'web' ? { cursor: 'pointer' } as any : {}),
  },
  rowBorder: { borderBottomWidth: 1, borderBottomColor: Colors.line },
  rowHovered: { backgroundColor: Colors.paper2 },
  templateCell: { flexDirection: 'row', alignItems: 'center', gap: 10 },
  tableCell: { flexDirection: 'row', alignItems: 'center' },
  templateIcon: {
    width: 28, height: 28, borderRadius: Radii.r2,
    justifyContent: 'center', alignItems: 'center',
    flexShrink: 0,
  },
  templateName: { fontSize: 13.5, fontWeight: FontWeight.semibold, color: Colors.ink },
  templateDesc: { fontSize: 11.5, color: Colors.ink3, marginTop: 1 },
  editedText: { fontSize: 12.5, color: Colors.ink3 },
  editedBy: { color: Colors.ink4 },
  editedNever: { fontSize: 12.5, color: Colors.ink4 },
  localeNote: {
    flexDirection: 'row', alignItems: 'center', gap: 10,
    marginTop: 20, padding: 12,
    backgroundColor: Colors.accentSoft,
    borderWidth: 1, borderColor: Colors.accentLine,
    borderRadius: Radii.r3,
  },
  localeNoteText: { flex: 1, fontSize: 12.5, color: Colors.accentInk },
  localeBold: { fontWeight: FontWeight.semibold },
});
